import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "bloc" }
const _hoisted_2 = { class: "custom-font-text" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = {
  key: 1,
  class: "profile-creation-wizard__content__avatar"
}
const _hoisted_5 = ["photo-url"]
const _hoisted_6 = { class: "bloc" }

import {RegisteredDependencies} from '@/profile-creation-wizard/common/DependencyInjection';
    import {eventBusProfileCreationWizard} from '@/profile-creation-wizard/common/events';
    import StepItemTitle from '@/profile-creation-wizard/common/ui/StepItemTitle.vue';
    import Step from '@/profile-creation-wizard/common/ui/step/Step.vue';
    import AccountUploadPhotoDialog from '@/profile-creation-wizard/components/for-completion/ui/5_description/AccountUploadPhotoDialog.vue';
    import DescriptionSide from '@/profile-creation-wizard/components/for-completion/ui/5_description/DescriptionSide.vue';
    import actions from '@/profile-creation-wizard/store/completion/actions';
    import getters from '@/profile-creation-wizard/store/completion/getters';
    import {Photo} from '@malt/asset-utils';
    import {pushVJoySnackbar, VJoyButton} from '@maltjoy/core-vue';
    import {useGlobalProperties, useTranslation} from '@malt/vue3-use';
    import {computed, inject, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, toRef} from 'vue';
    import {useRouter} from 'vue-router';
    import {useStore} from 'vuex';
    import Description from './Description.vue';

    type Form = {
        description: string;
    };

    
export default /*@__PURE__*/_defineComponent({
  __name: 'DescriptionStep',
  setup(__props) {

    const defaultForm: Form = {
        description: '',
    };

    const context = useGlobalProperties();
    const {msg} = useTranslation();
    const store = useStore();
    const router = useRouter();

    const trackingRepository = inject(RegisteredDependencies.injectionKeys.trackingRepository);

    const data = reactive<Form>(defaultForm);
    const descriptionRules = toRef(store.getters[getters.profileDescriptionRule]);

    const descriptionMinLength = computed(() => (data.description.length > 0 ? descriptionRules.value.min : 0));

    const descriptionMaxLength = computed(() => descriptionRules.value.max);

    const accountUploadPhotoDialog = ref<AccountUploadPhotoDialog>();
    const isLoadingButtonNext = ref(false);
    const isLoadingDescription = ref(true);
    const photo = ref<Photo>(null);

    const isStepOptional = computed(() => !store.state.isFunnelStrategy);
    const hasPhoto = computed(() => photo.value !== null);
    const isDescriptionValid = computed(
        () => data.description.trim().length >= descriptionRules.value.min && data.description.trim().length <= descriptionRules.value.max,
    );
    const isDescriptionEmpty = computed(() => data.description.trim().length === 0);
    const photoUrl = computed(() => {
        if (photo.value) {
            return new Photo(photo.value).getScaledUrl({width: 200});
        }
        return '';
    });

    const isPhotoValidOrOptionnal = computed(() => isStepOptional.value || hasPhoto.value);
    const isDescriptionValidOrOptionnal = computed(() => (isStepOptional.value && isDescriptionEmpty.value) || isDescriptionValid.value);
    const isStepValid = computed(() => isPhotoValidOrOptionnal.value && isDescriptionValidOrOptionnal.value);

    onBeforeMount(async () => {
        await fetchPhoto();
        isLoadingDescription.value = false;
    });

    onMounted(async () => {
        eventBusProfileCreationWizard.on('wizard:profile-photo-updated', switchPhotoUpload);

        data.description = store.getters[getters.description].content || '';
    });

    onBeforeUnmount(() => {
        eventBusProfileCreationWizard.off('wizard:profile-photo-updated', switchPhotoUpload);
    });

    async function fetchPhoto(): Promise<void> {
        await store.dispatch(actions.fetchPhoto, context.$pageContext.profileId);
        if (store.getters[getters.photo]) {
            hasPhoto.value = true;
            photo.value = store.getters[getters.photo];
        }
        isLoadingButtonNext.value = false;
    }

    async function handleUpdateDescription(): Promise<void> {
        await saveDescription();
    }

    async function saveDescription(): Promise<boolean> {
        // commented this line to prevent a bug blocking the click on next button :
        // focused on text area -> click next button -> blur event triggered -> saveDescription -> next button disabled while updating -> click triggered on disabled button
        // isLoadingButtonNext.value = true;

        try {
            if (isDescriptionValid.value && data.description != store.getters[getters.description]?.content) {
                await store.dispatch(actions.updateProfileDescription, data.description);
            }
        } catch (e) {
            pushVJoySnackbar({
                props: {
                    message: msg('profile.creation.common.notification.error.technical'),
                    level: 'error',
                },
            });
            return false;
        } finally {
            isLoadingButtonNext.value = false;
        }

        return true;
    }

    async function sentTrackingDescriptionEvent(): Promise<void> {
        await trackingRepository?.track(`description-length-${store.state.funnelMode}`, {
            descriptionLength: data.description.length,
        });
    }

    function switchPhotoUpload(receivedPhoto: any): void {
        photo.value = receivedPhoto.image;
    }

    async function openPhotoDialog(): Promise<void> {
        await accountUploadPhotoDialog.value.open();
    }

    async function goNext(): Promise<void> {
        isLoadingButtonNext.value = true;

        if (isDescriptionValid.value) {
            const isSaved = await saveDescription();
            if (isSaved) {
                await sentTrackingDescriptionEvent();
            }
        }
        router.push({name: router.currentRoute.value.meta.nextStep});

        isLoadingButtonNext.value = false;
    }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Step, {
    "is-step-valid": isStepValid.value,
    "is-loading": isLoadingButtonNext.value,
    "data-testid": "freelancer-signup-completion-description",
    onGoToNext: goNext
  }, {
    "step-content": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(StepItemTitle, null, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.photo.title')), 1)
          ]),
          _: 1
        }),
        _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.photo.subtitle')), 1),
        (!hasPhoto.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(_unref(VJoyButton), {
                circle: "",
                icon: "plus-circle",
                variant: "secondary",
                size: "large",
                "data-testid": "freelancer-signup-completion-description-cta-add-photo",
                onClick: openPhotoDialog
              })
            ]))
          : _createCommentVNode("", true),
        (hasPhoto.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("joy-avatar", {
                class: "picture-modify",
                size: "large",
                "photo-url": photoUrl.value,
                "data-testid": "freelancer-signup-completion-description-cta-update-photo",
                onClick: openPhotoDialog
              }, null, 8, _hoisted_5)
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_6, [
        (!isLoadingDescription.value)
          ? (_openBlock(), _createBlock(Description, {
              key: 0,
              modelValue: data.description,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((data.description) = $event)),
              "min-length": descriptionMinLength.value,
              "max-length": descriptionMaxLength.value,
              "onDescription:updated": handleUpdateDescription
            }, null, 8, ["modelValue", "min-length", "max-length"]))
          : _createCommentVNode("", true)
      ])
    ]),
    "step-side": _withCtx(() => [
      _createVNode(DescriptionSide)
    ]),
    "step-dialog": _withCtx(() => [
      _createVNode(AccountUploadPhotoDialog, {
        ref_key: "accountUploadPhotoDialog",
        ref: accountUploadPhotoDialog
      }, null, 512)
    ]),
    _: 1
  }, 8, ["is-step-valid", "is-loading"]))
}
}

})